/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import type { ReactNode } from 'react';

import { Text } from '@/shared/ui';
import { cn } from '@/shared/ui/utils/tailwind';

import { MicroLabel, SurfaceCard } from '../surface-card/SurfaceCard';

/**
 * The row of labelled figures that opens the deal / sale / lease detail
 * screens.
 *
 * One card rather than a grid of cards on purpose. Three or four figures in
 * separate boxes read as unrelated facts, and a CSS grid stretches every cell
 * to the tallest one, so a single long hint used to inflate the whole row.
 * Flattening them into one band also lets a figure be pushed to the right edge
 * without leaving a hole where its box used to be.
 */

export interface DetailStatItem {
  /** Stable key. Also the default testid suffix. */
  id: string;
  /** Eyebrow above the value. Already localized. */
  label: string;
  /** The figure or word. Already formatted — the strip never formats. */
  value: ReactNode;
  /**
   * Rendered inline after the value, a step smaller and lighter
   * ("de R$ 480.000"). Not a second line: it sits on the value's baseline.
   */
  secondary?: string;
  /**
   * A line under the value, saying where the figure comes from ("valor do
   * anúncio", "6% sobre a proposta"). Unlike `title` it is always visible,
   * so it can carry something the reader needs.
   */
  hint?: string;
  /**
   * CSS colour for the value, as a token reference (`'var(--accent-cobre)'`).
   * Omitted leaves the value on `--text-primary`.
   */
  accent?: string;
  /**
   * Pushes this item to the right edge. Set it on at most one item; items
   * after it follow it rightwards.
   */
  alignEnd?: boolean;
  /**
   * The full sentence, surfaced on hover, when the value alone is terse.
   * Hover-only, so never put here anything the reader actually needs.
   */
  title?: string;
  /**
   * The item's testid. Required wherever a screen's existing testids do not
   * derive from the strip's own — the sales strip is rooted at
   * `sale-kpi-cards` but its items must stay `sale-kpi-commission` and
   * friends. Falls back to `${stripTestId}-${id}`.
   */
  testId?: string;
}

export interface DetailStatStripProps {
  items: DetailStatItem[];
  /**
   * Right-aligned trailing slot, after every item: typically a sentence plus
   * a button. Rendered inside the same flex row, so it wraps with the items
   * rather than forming a second bar.
   */
  action?: ReactNode;
  className?: string;
  /** Root testid. Items without their own get `${testId}-${item.id}`. */
  testId?: string;
}

/**
 * One labelled figure inside the strip.
 *
 * @param props.item - The figure to render.
 * @param props.fallbackTestId - Used when the item carries no testid.
 * @returns The rendered item.
 */
function StatItem({ item, fallbackTestId }: { item: DetailStatItem; fallbackTestId?: string }) {
  return (
    <div
      data-testid={item.testId ?? fallbackTestId}
      title={item.title}
      className={cn('flex min-w-0 flex-col gap-(--space-0-5)', item.alignEnd && 'ml-auto')}
    >
      <MicroLabel>{item.label}</MicroLabel>

      <div className="flex flex-wrap items-baseline gap-(--space-1-5)">
        <Text
          component="span"
          style={item.accent ? { color: item.accent } : undefined}
          className={cn(
            'text-[length:var(--text-stat)] [font-weight:var(--font-semibold)]',
            'leading-tight tabular-nums',
            !item.accent && 'text-(--text-primary)',
          )}
        >
          {item.value}
        </Text>
        {item.secondary && (
          <Text
            component="span"
            className="text-[length:var(--text-meta)] tabular-nums text-(--text-tertiary-soft)"
          >
            {item.secondary}
          </Text>
        )}
      </div>

      {item.hint && (
        <Text
          component="span"
          className="text-[length:var(--text-meta)] text-(--text-tertiary-soft)"
        >
          {item.hint}
        </Text>
      )}
    </div>
  );
}

/**
 * A horizontal strip of labelled figures in one card.
 *
 * @param props.items - The figures, in reading order.
 * @param props.action - Optional trailing right-aligned slot.
 * @param props.className - Extra classes for the card.
 * @param props.testId - Root testid.
 * @returns The rendered strip.
 */
export function DetailStatStrip({
  items,
  action,
  className,
  testId = 'detail-stat-strip',
}: DetailStatStripProps) {
  return (
    <SurfaceCard testId={testId} className={cn('px-(--space-5) py-(--space-4)', className)}>
      {/*
        The figures hang from a shared top edge, so every label sits on
        one line and every number on the next. Centred, an item carrying
        a hint grew taller than its neighbours and pulled its own label
        and value out of line with theirs -- five stats, no two of them
        agreeing on where a row began.

        The actions stay centred against the tallest figure. Pinned to
        the top they floated against a block twice their height, reading
        as a second row that failed to line up.
      */}
      <div className="flex flex-wrap items-start gap-(--space-3-5) gap-x-(--space-7)">
        {items.map((item) => (
          <StatItem key={item.id} item={item} fallbackTestId={`${testId}-${item.id}`} />
        ))}
        {action && (
          <div className="ml-auto flex flex-wrap items-center justify-end gap-(--space-3) self-center">
            {action}
          </div>
        )}
      </div>
    </SurfaceCard>
  );
}
